import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { translations, detectLanguage } from "./i18n";
import api from "./api";

const AppContext = createContext(null);

const FALLBACK_BRAND = {
    brand_name: "Delicias Dominicanas",
    brand_tagline: "Montego Bay",
    brand_initials: "DD",
    brand_color_primary: "#C85A17",
    brand_color_accent: "#4A7C59",
    country_code: "DR",
    country_preset: { name: "Rep. Dominicana", stripes: ["#002D62", "#FFFFFF", "#CE1126"], cuisine_label_es: "Cocina Dominicana", cuisine_label_en: "Dominican Cuisine" },
    decorations_enabled: true,
    hero_headline_es: "Sabor isla, hecho en casa.",
    hero_headline_en: "Island flavor, made at home.",
    hero_subtitle_es: "",
    hero_subtitle_en: "",
    hero_cta_primary_es: "Ordenar ahora",
    hero_cta_primary_en: "Order Now",
    hero_cta_secondary_es: "Ver el menú",
    hero_cta_secondary_en: "See the Menu",
    hero_image_url: "",
    chef_name: "Milly",
    chef_subtitle_es: "Cocinado con amor por Chef Milly",
    chef_subtitle_en: "Cooked with love by Chef Milly",
    chef_bio_es: "",
    chef_bio_en: "",
    chef_image_url: "",
    contact_city: "Montego Bay, Jamaica",
    contact_hours_es: "Mar – Dom · 10:00 AM – 9:00 PM",
    contact_hours_en: "Tue – Sun · 10:00 AM – 9:00 PM",
    contact_address: "Montego Bay, Jamaica",
    announcement_enabled: true,
    announcement_text_es: "",
    announcement_text_en: "",
    currency_symbol: "$",
    currency_code: "USD",
    currency_position: "before",
    hero_badge1_es: "Recetas de familia",
    hero_badge1_en: "Family Recipes",
    hero_badge2_es: "Ingredientes frescos",
    hero_badge2_en: "Fresh Ingredients",
    hero_badge3_es: "Hecho a mano",
    hero_badge3_en: "Handmade",
    about_title_es: "",
    about_title_en: "",
    about_body1_es: "",
    about_body1_en: "",
    about_body2_es: "",
    about_body2_en: "",
    about_stat1_value: "20+",
    about_stat1_label_es: "Años cocinando",
    about_stat1_label_en: "Years cooking",
    about_stat2_value: "500+",
    about_stat2_label_es: "Familias felices",
    about_stat2_label_en: "Happy families",
    about_stat3_value: "15+",
    about_stat3_label_es: "Recetas",
    about_stat3_label_en: "Recipes",
};

export const AppProvider = ({ children }) => {
    const [lang, setLang] = useState("es");
    const [cart, setCart] = useState([]);
    const [cartOpen, setCartOpen] = useState(false);
    const [branding, setBranding] = useState(FALLBACK_BRAND);

    // ----- Tenant resolution -----
    // SaaS storefronts live under /r/{slug}/*. Anything else is "legacy mode"
    // and uses the un-prefixed /api routes (the original Delicias site).
    const tenantSlug = useMemo(() => {
        if (typeof window === "undefined") return null;
        const m = window.location.pathname.match(/^\/r\/([a-z0-9][a-z0-9-]{0,30}[a-z0-9])\b/i);
        return m ? m[1].toLowerCase() : null;
    }, []);
    const apiBase = tenantSlug ? `/t/${tenantSlug}` : "";
    const isTenantMode = !!tenantSlug;

    useEffect(() => {
        setLang(detectLanguage());
        try {
            const saved = localStorage.getItem("cart");
            if (saved) setCart(JSON.parse(saved));
        } catch {}
    }, []);

    // Load public branding/config and inject CSS variables for the brand colors
    useEffect(() => {
        const url = isTenantMode ? `/t/${tenantSlug}/config` : "/config";
        api.get(url)
            .then((r) => {
                if (r.data?.branding) setBranding({ ...FALLBACK_BRAND, ...r.data.branding });
            })
            .catch(() => {});
    }, [isTenantMode, tenantSlug]);
    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty("--brand-primary", branding.brand_color_primary);
        root.style.setProperty("--brand-accent", branding.brand_color_accent);
        const stripes = branding.country_preset?.stripes || ["#002D62", "#FFFFFF", "#CE1126"];
        root.style.setProperty("--flag-1", stripes[0]);
        root.style.setProperty("--flag-2", stripes[1]);
        root.style.setProperty("--flag-3", stripes[2]);
        // Update document title for SEO
        if (branding.brand_name) {
            document.title = `${branding.brand_name} · ${branding.brand_tagline || ""}`.trim();
        }
    }, [branding]);

    useEffect(() => {
        try {
            localStorage.setItem("cart", JSON.stringify(cart));
        } catch {}
    }, [cart]);

    const t = useMemo(() => translations[lang] || translations.es, [lang]);

    const changeLang = useCallback((next) => {
        setLang(next);
        try {
            localStorage.setItem("lang", next);
        } catch {}
    }, []);

    const addToCart = useCallback((item, opts = {}) => {
        const addQty = Math.max(1, parseInt(opts.quantity || item.default_quantity || 1) || 1);
        const preOrder = !!opts.pre_order;
        setCart((prev) => {
            const found = prev.find(
                (c) => c.item_id === item.id && !!c.pre_order === preOrder
            );
            if (found) {
                return prev.map((c) =>
                    c.item_id === item.id && !!c.pre_order === preOrder
                        ? { ...c, quantity: c.quantity + addQty }
                        : c
                );
            }
            return [
                ...prev,
                {
                    item_id: item.id,
                    slug: item.slug,
                    name_es: item.name_es,
                    name_en: item.name_en,
                    price: item.price,
                    image_url: item.image_url,
                    quantity: addQty,
                    pre_order: preOrder,
                },
            ];
        });
    }, []);

    const updateQty = useCallback((item_id, delta, preOrder = false) => {
        setCart((prev) =>
            prev
                .map((c) =>
                    c.item_id === item_id && !!c.pre_order === !!preOrder
                        ? { ...c, quantity: Math.max(0, c.quantity + delta) }
                        : c
                )
                .filter((c) => c.quantity > 0)
        );
    }, []);

    const removeItem = useCallback((item_id, preOrder = false) => {
        setCart((prev) =>
            prev.filter(
                (c) => !(c.item_id === item_id && !!c.pre_order === !!preOrder)
            )
        );
    }, []);

    const clearCart = useCallback(() => setCart([]), []);

    const cartTotal = useMemo(
        () => cart.reduce((sum, c) => sum + c.price * c.quantity, 0),
        [cart]
    );
    const cartCount = useMemo(
        () => cart.reduce((sum, c) => sum + c.quantity, 0),
        [cart]
    );

    /** Format a number with the configured currency. */
    const fmtMoney = useCallback(
        (n) => {
            const value = Number(n || 0).toFixed(2);
            const sym = branding.currency_symbol || "$";
            return branding.currency_position === "after" ? `${value} ${sym}` : `${sym}${value}`;
        },
        [branding.currency_symbol, branding.currency_position]
    );

    const value = {
        lang,
        changeLang,
        t,
        branding,
        fmtMoney,
        cart,
        cartOpen,
        setCartOpen,
        addToCart,
        updateQty,
        removeItem,
        clearCart,
        cartTotal,
        cartCount,
        // SaaS tenant context — empty for legacy site, set for /r/{slug}/*
        tenantSlug,
        isTenantMode,
        apiBase,
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error("useApp must be used within AppProvider");
    return ctx;
};
